import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button, Card, Upload, message, Tabs, Alert } from "antd";
import {
  UploadOutlined,
  ArrowLeftOutlined,
  ShopOutlined,
  UserOutlined,
  TeamOutlined,
  CarryOutOutlined,
  AppstoreOutlined,
  BoxPlotOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

// Adjust these paths if your folder structure is slightly different
import StickyBar from "../../common/StickyBar";
import CommonFooter from "../../common/footer/commonFooter";
import { importStores } from "../../redux/store.slice";
import { importUsers } from "../../redux/user.slice";
import { importCustomers } from "../../redux/customer.slice";
import { importSuppliers } from "../../redux/supplier.slice"; 
import { importGroups } from "../../redux/group.slice";
import { importModels } from "../../redux/model.slice"; 
import { importProducts } from "../../redux/product.slice";

const CompanySetup = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [uploading, setUploading] = useState({});

  // Handler to dispatch Redux actions based on the active tab
  const handleUpload = async (info, entityKey) => {
    if (info.file.status === "uploading") {
      setUploading((prev) => ({ ...prev, [entityKey]: true }));
      return;
    }

    if (info.file.status === "done") {
      const file = info.file.originFileObj;

      try {
        // --- SHOPS IMPORT LOGIC ---
        if (entityKey === "shops") {
          await dispatch(importStores(file)).unwrap();
          message.success(`${info.file.name} imported successfully for Shops`);
        }
        // --- USERS IMPORT LOGIC ---
        else if (entityKey === "users") {
          await dispatch(importUsers(file)).unwrap();
          message.success(`${info.file.name} imported successfully for Users`);
        }
        // --- CUSTOMERS IMPORT LOGIC ---
        else if (entityKey === "customers") {
          await dispatch(importCustomers(file)).unwrap();
          message.success(
            `${info.file.name} imported successfully for Customers`,
          );
        }
        // --- SUPPLIERS IMPORT LOGIC ---
        else if (entityKey === "suppliers") {
          await dispatch(importSuppliers(file)).unwrap();
          message.success(
            `${info.file.name} imported successfully for Suppliers`,
          );
        }
        // --- FUTURE ENTITIES LOGIC ---
        else if (entityKey === "categories") {
          await dispatch(importGroups(file)).unwrap();
          message.success(`${info.file.name} imported successfully for Groups`);
        }
        // --- MODELS IMPORT LOGIC ---
        else if (entityKey === "models") {
          await dispatch(importModels(file)).unwrap();
          message.success(`${info.file.name} imported successfully for Models`);
        }
        // --- PRODUCTS IMPORT LOGIC ---
        else if (entityKey === "products") {
          await dispatch(importProducts(file)).unwrap();
          message.success(`${info.file.name} imported successfully for Products`);
        }
      } catch (error) {
        console.error(`Import failed for ${entityKey}:`, error);
        const errorMsg =
          error?.message ||
          `Failed to import ${entityKey}. Please check the file format.`;
        message.error(errorMsg);
      } finally {
        setUploading((prev) => ({ ...prev, [entityKey]: false }));
      }
    } else if (info.file.status === "error") {
      setUploading((prev) => ({ ...prev, [entityKey]: false }));
      message.error(`${info.file.name} upload failed.`);
    }
  };

  // Define the entities with their translation keys
  const entities = [
    {
      key: "shops",
      labelKey: "companies.setup.entities.shops",
      icon: <ShopOutlined />,
      required: true,
    },
    {
      key: "users",
      labelKey: "companies.setup.entities.users",
      icon: <UserOutlined />,
      required: true,
    },
    {
      key: "customers",
      labelKey: "companies.setup.entities.customers",
      icon: <TeamOutlined />,
      required: false,
    },
    {
      key: "suppliers",
      labelKey: "companies.setup.entities.suppliers",
      icon: <CarryOutOutlined />,
      required: false,
    },
    {
      key: "categories",
      labelKey: "companies.setup.entities.categories",
      icon: <AppstoreOutlined />,
      required: true,
    },
    {
      key: "models",
      labelKey: "companies.setup.entities.models",
      icon: <BoxPlotOutlined />,
      required: true,
    },
    {
      key: "products",
      labelKey: "companies.setup.entities.products",
      icon: <ShoppingOutlined />,
      required: true,
    },
  ];

  const tabItems = entities.map((entity) => {
    const entityName = t(entity.labelKey);

    return {
      key: entity.key,
      label: (
        <span>
          {entity.icon} {entityName}{" "}
          {!entity.required && (
            <span style={{ fontSize: 10, color: "#999", marginLeft: 4 }}>
              {t("companies.setup.optional")}
            </span>
          )}
        </span>
      ),
      children: (
        <Card
          bordered={false}
          style={{
            maxWidth: 600,
            margin: "24px auto",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          <Alert
            message={t("companies.setup.importAlertTitle", {
              entity: entityName,
            })}
            description={t("companies.setup.importAlertDesc", {
              entity: entityName,
            })}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Upload
            name="file"
            // We use customRequest to prevent Ant Design from auto-uploading.
            // We handle the upload manually via Redux in handleUpload.
            customRequest={({ onSuccess }) => {
              // This is a dummy success to let Ant Design know the "upload" phase is done,
              // triggering the 'done' status in onChange.
              setTimeout(() => onSuccess("ok"), 100);
            }}
            onChange={(info) => handleUpload(info, entity.key)}
            showUploadList={true}
            accept=".xlsx, .xls"
            maxCount={1}
            disabled={uploading[entity.key]}
          >
            <Button
              icon={<UploadOutlined />}
              loading={uploading[entity.key]}
              type="primary"
              size="large"
              style={{ width: "100%" }}
            >
              {t("companies.setup.uploadButton", { entity: entityName })}
            </Button>
          </Upload>

          <div
            style={{
              marginTop: 24,
              padding: 16,
              background: "#f9f9f9",
              borderRadius: 8,
              border: "1px solid #eee",
            }}
          >
            <h6
              style={{
                margin: "0 0 8px 0",
                display: "flex",
                alignItems: "center",
              }}
            >
              <i className="ti ti-info-circle me-1"></i>
              {t("companies.setup.instructions")}
            </h6>
            <ul
              style={{
                margin: 0,
                paddingLeft: 20,
                color: "#666",
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              <li>{t("companies.setup.rule1")}</li>
              <li>{t("companies.setup.rule2")}</li>
              <li>{t("companies.setup.rule3")}</li>
            </ul>
          </div>
        </Card>
      ),
    };
  });

  return (
    <div className="page-wrapper">
      <div className="content">
        <StickyBar
          title={t("companies.setup.title") || "Company Setup & Import"}
          actions={
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/${shopId}/companies`)}
            >
              {t("companies.setup.back") || "Back to Companies"}
            </Button>
          }
        />

        <div className="card table-list-card">
          <div className="card-body p-4">
            <Tabs
              defaultActiveKey="shops"
              items={tabItems}
              size="large"
              style={{ marginTop: "8px" }}
            />
          </div>
        </div>
      </div>
      <CommonFooter />
    </div>
  );
};

export default CompanySetup;
